import type { TFunction } from 'i18next';
import type { DrawnOpeningInfo } from '@draughts/shared';
import type { DrawnOpeningPayload } from '../services/socket';
import {
  BALLOT_CEILING_SIZES,
  OPENING_SEQUENCE_COUNTS,
  ballotCeiling,
  ballotNumber,
  isDrawnOpening,
  openingPlies,
  resolveOpeningMode,
  trovaApertura,
  tabellaAperture,
  type OpeningMode,
} from '@draughts/shared';

/**
 * How many openings a mode draws from, for the variant it is played on: the size
 * of the ballot pool under that strength ceiling, or the number of legal
 * sequences of that many half-moves. `null` when we have no number for it — an
 * unknown variant slug, or a variant added before the counts were regenerated.
 */
function poolSize(opening: OpeningMode, variant: string): number | null {
  const ceiling = ballotCeiling(opening);
  if (ceiling !== null) {
    const sizes = BALLOT_CEILING_SIZES[variant as keyof typeof BALLOT_CEILING_SIZES];
    return sizes?.[ceiling - 1] ?? null;
  }
  const plies = openingPlies(opening);
  if (plies === 0) return null;
  return OPENING_SEQUENCE_COUNTS[variant]?.[plies - 1] ?? null;
}

/**
 * Label of an "Apertura" option: what it draws, plus how many openings it draws
 * from on this variant — "Tabella A+B - 3N/3B (166)", "3 mosse a caso qualsiasi (302)".
 *
 * The count is appended here rather than interpolated into the translation for
 * two reasons: a parenthesised number reads the same in all 7 locales (like the
 * `min` / `+N s` units in the same modal), and a `{{count}}` placeholder would
 * put i18next into plural-resolution mode, which these keys do not define. It
 * also means a variant with no generated count degrades to the bare name instead
 * of rendering an empty bracket.
 *
 * Shared by the challenge modal and by every read-only view of a challenge, so a
 * player is offered and then shown the exact same wording.
 */
export function openingOptionLabel(
  t: TFunction,
  opening: OpeningMode,
  variant: string,
  { withCount = true, openingId }: { withCount?: boolean; openingId?: number | null } = {},
): string {
  // Variant ids reach the UI in both casings — the lowercase slug from the
  // dropdowns and the uppercase `GameVariant` carried by challenge objects.
  const slug = String(variant).toLowerCase();
  // Ceiling 3 is the one table whose name differs between the two ballots:
  // Italian A ∪ B (166), English A alone (156).
  const tables = slug === 'english' ? 'A' : 'A+B';
  const base = t(`online.challenge.opening_${opening}`, { tables });
  /*
   * L'apertura unica dice QUALE, e non un conteggio.
   *
   * Le altre otto si descrivono con la larghezza del pool — «(126)» vuol dire
   * «una fra centoventisei» — perche' prima della pescata non c'e' altro da
   * dire. Questa il pool non ce l'ha: pesca da una sola apertura, e scrivere
   * «(1)» sarebbe un numero vero e inutile. Al suo posto va il numero che
   * l'arbitro annuncia, con la forza fra parentesi come nelle tabelle.
   *
   * Senza `openingId` resta il nome nudo: chi chiama non lo aveva — una riga
   * arrivata da un Node vecchio durante un deploy, o la tendina del modale, che
   * l'apertura non l'ha ancora scelta.
   */
  if (opening === 'single') {
    const scelta = trovaApertura(slug, openingId);
    return scelta
      ? `${base} — ${t('tournament.create.aperturaUnicaScelta', { id: scelta.id })} (${scelta.forza})`
      : base;
  }
  if (!withCount) return base;
  const size = poolSize(opening, slug);
  return size === null ? base : `${base} (${size})`;
}

/**
 * Una riga della tendina dell'apertura unica: numero, mosse e valutazione.
 *
 * ── Perche' dice tutti e tre ────────────────────────────────────────────────
 *
 * Le 174 voci si distinguono solo per tre numeri, e nessuna di loro si spiega
 * col proprio nome: «n. 42» non dice niente a chi non ha la tabella aperta
 * accanto. La riga porta quindi il numero che l'arbitro annuncia, le tre
 * semi-mosse e il codice di forza — «n. 42 — 21-17 9-13 25-21 (1B)».
 *
 * ── Perche' le mosse vengono dalla TABELLA ──────────────────────────────────
 *
 * Da `BallotOpening.moves`, cioe' dalla notazione del Regolamento, e non
 * rigiocandole col motore: il motore scrive le catture con la `x` (`17x10`), il
 * regolamento col trattino (`17-10`). In una riga che cita un numero di tabella
 * la notazione giusta e' quella della tabella — e cosi' la tendina non si tira
 * dietro l'enumerazione delle 302 sequenze legali per scrivere tre numeri.
 *
 * Sul valore vuoto torna l'invito a sceglierne una: e' il solo caso in cui la
 * tendina non sta mostrando una scelta.
 */
export function etichettaAperturaUnica(t: TFunction, variant: string, id: string): string {
  const scelta = trovaApertura(variant, Number(id));
  if (!scelta) return t('tournament.create.aperturaUnicaScegli');
  const numero = t('tournament.create.aperturaUnicaScelta', { id: scelta.id });
  return `${numero} — ${scelta.moves.join(' ')} (${scelta.forza})`;
}

/** I numeri delle aperture della variante, nell'ordine della tabella federale. */
export function numeriDelleAperture(variant: string): string[] {
  return tabellaAperture(variant).map((o) => String(o.id));
}

/**
 * Il testo della pastiglia dell'apertura, o `null` quando non c'e' niente da
 * annunciare.
 *
 * Tre sedi la scrivevano inline con lo stesso trio — `resolveOpeningMode`,
 * `isDrawnOpening`, `openingOptionLabel` — e ne servivano altre due. Il trio
 * NON e' una formalita': `resolveOpeningMode` va chiamata PRIMA di
 * `isDrawnOpening` perche' su una variante senza tabella federale il server
 * degrada a tre semi-mosse a caso, e senza la stessa degradazione qui la
 * pastiglia direbbe «Tabella B» sotto una gara che tabella B non gioca. Con
 * cinque copie, la quinta e' quella che lo dimentica.
 *
 * `null` e non stringa vuota: chi chiama deve DECIDERE di non disegnare la
 * pastiglia, non disegnarne una vuota.
 *
 * ⚠️ La variante arriva facoltativa dal filo su piu' di una riga, e
 * `resolveOpeningMode` sceglierebbe la degradazione su `undefined`: il ripiego
 * e' lo stesso che le card usano gia' per l'etichetta della variante.
 */
export function pastigliaApertura(
  t: TFunction,
  openingMode: OpeningMode | null | undefined,
  variant: string | null | undefined,
  openingId?: number | null,
): string | null {
  const suQuale = variant || 'international';
  const apertura = resolveOpeningMode(openingMode ?? 'free', suQuale);
  if (!isDrawnOpening(apertura)) return null;
  return openingOptionLabel(t, apertura, suQuale, { withCount: false, openingId });
}

/**
 * Il cartellino da mostrare per questa apertura, o `null` se non c'e' sorteggio.
 *
 * Il CONTO delle semi-mosse non passa di qui: quello guarda `opening.moves` a
 * prescindere dal modo, perche' sono comunque mosse che non hanno giocato i due. Il
 * cartellino invece e' solo del sorteggio, ed e' la stessa condizione che i tre store
 * ripetevano in quattro punti.
 *
 * Vive qui e non in `services/socket`, dove sta il tipo del payload, per una ragione
 * pratica: quel modulo e' rimpiazzato da un mock in ventotto file di test, e ogni
 * export nuovo la' dentro fa fallire tutti quelli che non lo elencano. Il tipo arriva
 * come `import type`, che si cancella in compilazione e non porta dipendenze.
 */
export function annuncioApertura(opening?: DrawnOpeningPayload | null): DrawnOpeningInfo | null {
  if (opening?.mode !== 'drawn') return null;
  const { mode: _mode, ...info } = opening;
  return info;
}

/**
 * L'apertura del rientro, ma con l'OGGETTO di prima quando e' la stessa.
 *
 * Il cartellino dell'apertura (`DrawnOpeningBanner`) si riaccende per 5 secondi a
 * ogni oggetto nuovo: un rientro dopo un buco di rete, a meta' partita, lo farebbe
 * ricomparire sopra la damiera. Stessa stanza, stessa apertura → stesso oggetto.
 * Dopo un ricarico della pagina lo store riparte vuoto e il cartellino si rivede:
 * e' il primo annuncio che quella pagina riceve.
 */
export function aperturaStabile(
  attuale: DrawnOpeningInfo | null,
  arrivata: DrawnOpeningInfo | null,
): DrawnOpeningInfo | null {
  if (!attuale || !arrivata) return arrivata;
  const uguali =
    attuale.id === arrivata.id &&
    (attuale.moves ?? []).join(' ') === (arrivata.moves ?? []).join(' ');
  return uguali ? attuale : arrivata;
}

/**
 * Il codice compatto della forza, come lo scrivono le tabelle federali: «PARI»
 * diventa «P», «1N»/«2B» restano, e la vecchia forma a lettere («NN») diventa
 * «2N». `null` per una forza assente o che non si riconosce: meglio nessun codice
 * che uno inventato.
 */
export function codiceForza(forza?: string | null): string | null {
  if (!forza) return null;
  const f = forza.trim().toUpperCase();
  if (f === 'PARI' || f === 'P') return 'P';
  if (/^[1-4][NB]$/.test(f)) return f;
  if (/^(N{1,4}|B{1,4})$/.test(f)) return `${f.length}${f[0]}`;
  return null;
}

/** Il titolo di un'apertura sorteggiata, in due pezzi perche' il codice non va a capo. */
export interface TitoloApertura {
  /** «Apertura N° 56», oppure «Apertura sorteggiata» quando non c'e' una riga dietro. */
  titolo: string;
  /** Il codice di forza compatto, o `null` quando non c'e' un numero a cui riferirlo. */
  codice: string | null;
}

/**
 * Il titolo dell'apertura come lo annuncia l'arbitro: il numero di tabella e,
 * subito dopo, la forza di quella riga — «APERTURA N° 56 (1N)», le pari «(P)».
 *
 * ── Perche' i due non si separano ───────────────────────────────────────────
 *
 * Una riga del Regolamento e' identificata da ENTRAMBI: le tabelle la elencano
 * per numero e per forza, e l'arbitro li annuncia insieme. Due sedi su tre
 * mostravano il solo numero, e non per una scelta: il cartellino sulla damiera
 * e quello pre-partita si riscrivevano il titolo ciascuno per conto proprio, e
 * la forza l'aveva aggiunta solo la terza, la lista mosse. Ora il titolo lo
 * compone questa, e le tre lo leggono.
 *
 * ── Perche' il numero passa da `ballotNumber` ───────────────────────────────
 *
 * E non da `id !== undefined`. `randomDrawnOpening` mette `id` a 0 e il libro
 * International dei bot ci mette l'INDICE della propria lista (0-5): un
 * «Apertura n. 3» inventato e' peggio di nessun numero. A monte lo garantisce
 * gia' `drawnOpeningInfo`, che l'`id` lo emette solo con la forza — ma quel
 * filtro viveva in due copie, una per sede, e la terza non ce l'aveva affatto.
 *
 * ── Perche' due campi e non una stringa ─────────────────────────────────────
 *
 * Il codice vuole uno `<span>` suo con `white-space: nowrap`: a 360px, dentro
 * una pastiglia larga quanto la damiera, «(1N)» si stacca dal numero e va a
 * capo da solo.
 */
export function titoloApertura(
  t: TFunction,
  opening: { id?: number; forza?: string } | null | undefined,
): TitoloApertura | null {
  if (!opening) return null;
  const numero = ballotNumber({ id: opening.id ?? 0, forza: opening.forza ?? '' });
  return numero === undefined
    ? { titolo: t('game.sidebar.openingDrawn', 'Drawn opening'), codice: null }
    : {
        titolo: t('game.sidebar.openingNumbered', 'Opening No. {{n}}', { n: numero }),
        codice: codiceForza(opening.forza),
      };
}
